import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaInstagram, FaExternalLinkAlt } from "react-icons/fa";

// ── Instagram embed loader ───────────────────────────────────────────────────
// Converts any Instagram URL to the embed URL format
const toEmbedUrl = (url) => {
  if (!url) return null;
  // Normalize: strip trailing slash, query params
  const clean = url.split("?")[0].replace(/\/$/, "");
  return `${clean}/embed/`;
};

// ── Single Instagram card ────────────────────────────────────────────────────
const InstagramCard = ({ story, index, onDeleteClick, accentColor, isDark, cardBg, borderColor, textSecondary }) => {
  const navigate = useNavigate();
  const embedUrl = toEmbedUrl(story.urlInstagram);

  return (
    <motion.div
      key={story.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="rounded-xl border overflow-hidden flex flex-col group relative"
      style={{ backgroundColor: cardBg, borderColor }}
    >
      {/* Action buttons overlay */}
      <div className="absolute top-3 right-3 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
        <button
          onClick={() => navigate(`/stories/edit/${story.id}`)}
          className="p-2 rounded-lg shadow-md transition-all duration-200 hover:scale-110"
          style={{ backgroundColor: accentColor, color: "#fff" }}
          title="Edit stories"
        >
          <FaEdit className="text-xs" />
        </button>
        <button
          onClick={() => onDeleteClick(story)}
          className="p-2 rounded-lg shadow-md transition-all duration-200 hover:scale-110"
          style={{ backgroundColor: "#dc2626", color: "#fff" }}
          title="Hapus stories"
        >
          <FaTrash className="text-xs" />
        </button>
      </div>

      {/* Instagram Embed */}
      <div className="relative w-full overflow-hidden" style={{ minHeight: "480px" }}>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full border-0"
            style={{ minHeight: "580px", display: "block" }}
            scrolling="no"
            allowTransparency="true"
            allow="encrypted-media"
            title={`Instagram post ${story.id}`}
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center gap-3 p-6"
            style={{ minHeight: "480px", backgroundColor: `${accentColor}08` }}
          >
            <FaInstagram className="text-4xl" style={{ color: "#E1306C" }} />
            <p className="text-xs text-center" style={{ color: textSecondary }}>
              URL Instagram tidak valid
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between px-4 py-3 border-t"
        style={{ borderColor }}
      >
        <div className="flex items-center gap-2">
          <FaInstagram className="text-sm" style={{ color: "#E1306C" }} />
          <span className="text-xs font-medium" style={{ color: textSecondary }}>
            Instagram Post
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Open in Instagram */}
          <a
            href={story.urlInstagram}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110"
            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            title="Buka di Instagram"
          >
            <FaExternalLinkAlt className="text-xs" />
          </a>
          {/* Mobile actions */}
          <div className="flex gap-1.5 md:hidden">
            <button
              onClick={() => navigate(`/stories/edit/${story.id}`)}
              className="p-1.5 rounded-lg transition-all duration-300"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              <FaEdit className="text-xs" />
            </button>
            <button
              onClick={() => onDeleteClick(story)}
              className="p-1.5 rounded-lg transition-all duration-300"
              style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}
            >
              <FaTrash className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── InstagramComponent ───────────────────────────────────────────────────────
const InstagramComponent = ({
  posts,
  onDeleteClick,
  accentColor,
  isDark,
  cardBg,
  borderColor,
  textPrimary,
  textSecondary,
}) => {
  if (posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {posts.map((story, index) => (
        <InstagramCard
          key={story.id}
          story={story}
          index={index}
          onDeleteClick={onDeleteClick}
          accentColor={accentColor}
          isDark={isDark}
          cardBg={cardBg}
          borderColor={borderColor}
          textSecondary={textSecondary}
        />
      ))}
    </div>
  );
};

export default InstagramComponent;